import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  Ban, CalendarCheck, CalendarClock, CheckCircle2, Clock, Flame, MessageSquareText, Pencil, Plus, Snowflake, ThermometerSun,
  Trash2, UserX,
} from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { FormField } from '@/components/FormField';
import { Select } from '@/components/Select';
import { Textarea } from '@/components/Textarea';
import { StatusPill } from '@/components/StatusPill';
import { EmptyState } from '@/components/EmptyState';
import { Pagination } from '@/components/Pagination';
import { LoadErrorState, NoMatchesState } from '@/components/ListStates';
import { useAuth } from '@/hooks/useAuth';
import { useLabels } from '@/contexts/WorkspaceContext';
import {
  CompleteViewingInput, VIEWING_STATUSES, Viewing, ViewingInterest, ViewingStatus, ViewingsSummary, realEstateService,
} from '@/services/real-estate.service';
import ScheduleViewingModal from './ScheduleViewingModal';
import {
  INTEREST_CLASS, INTEREST_LABEL, Kpi, Pill, VIEWING_STATUS_LABEL, VIEWING_STATUS_VARIANT, dayInZone, personName, placeLine,
  timeInZone, unitSpecs, useCompanyTimezone, useRealEstateAccess, useTeam,
} from './shared';

type Tab = 'today' | 'upcoming' | 'past';
const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'today', label: 'Today' },
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'past', label: 'Past' },
];
const LIMIT = 25;
const INTEREST_ICON: Record<ViewingInterest, typeof Flame> = { hot: Flame, warm: ThermometerSun, cold: Snowflake };

const iconBtn = 'flex h-8 w-8 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-surface-alt disabled:opacity-40';

export default function ViewingsPage() {
  const t = useLabels();
  const { user } = useAuth();
  const tz = useCompanyTimezone();
  const access = useRealEstateAccess();
  const team = useTeam();
  const [params, setParams] = useSearchParams();
  const tab: Tab = (['today', 'upcoming', 'past'] as const).find((x) => x === params.get('tab')) ?? 'today';

  const [status, setStatus] = useState<ViewingStatus | ''>('');
  const [agent, setAgent] = useState('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<Viewing[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [summary, setSummary] = useState<ViewingsSummary | null>(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [editing, setEditing] = useState<Viewing | null>(null);
  const [completing, setCompleting] = useState<Viewing | null>(null);
  const [busyUid, setBusyUid] = useState<string | null>(null);
  const reqId = useRef(0);

  const filtered = status !== '' || agent !== '';

  const load = useCallback(async () => {
    const id = ++reqId.current;
    setLoading(true);
    setError(false);
    try {
      const r = await realEstateService.listViewings({
        when: tab, status: status || undefined, agentUserUid: agent || undefined, page, limit: LIMIT,
      });
      if (id !== reqId.current) return;
      setItems(r.items);
      setTotal(r.total);
    } catch {
      if (id === reqId.current) setError(true);
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [tab, status, agent, page]);

  const loadSummary = useCallback(() => {
    realEstateService.viewingsSummary().then(setSummary).catch(() => { /* tiles keep their last numbers */ });
  }, []);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const refresh = () => { void load(); loadSummary(); };

  const setTab = (next: Tab, nextStatus: ViewingStatus | '' = '') => {
    const p = new URLSearchParams(params);
    p.set('tab', next);
    setParams(p, { replace: true });
    setStatus(nextStatus);
    setPage(1);
  };
  const clearFilters = () => { setStatus(''); setAgent(''); setPage(1); };

  const act = async (v: Viewing, key: string, fn: () => Promise<unknown>, done: string) => {
    setBusyUid(`${v.viewingUid}:${key}`);
    try {
      await fn();
      toast.success(done);
      refresh();
    } catch { /* interceptor toasts */ } finally {
      setBusyUid(null);
    }
  };

  const cancel = (v: Viewing) => {
    if (!window.confirm(`Cancel the viewing of ${v.unit?.label ?? 'this unit'} with ${v.customerName || v.customerPhone || 'the customer'}?`)) return;
    void act(v, 'cancel', () => realEstateService.cancelViewing(v.viewingUid), 'Viewing cancelled');
  };
  const noShow = (v: Viewing) => {
    if (!window.confirm(`Mark ${v.customerName || v.customerPhone || 'the customer'} as a no-show?`)) return;
    void act(v, 'noshow', () => realEstateService.noShowViewing(v.viewingUid), 'Marked as no-show');
  };
  const remove = (v: Viewing) => {
    if (!window.confirm(`Delete this viewing of ${v.unit?.label ?? 'the unit'}? It disappears from every list.`)) return;
    void act(v, 'delete', () => realEstateService.removeViewing(v.viewingUid), 'Viewing deleted');
  };

  // Past and upcoming lists read better split by day.
  const groups = useMemo(() => {
    if (tab === 'today') return [{ day: '', rows: items }];
    const out: Array<{ day: string; rows: Viewing[] }> = [];
    for (const v of items) {
      const day = dayInZone(v.scheduledAt, tz);
      const last = out[out.length - 1];
      if (last && last.day === day) last.rows.push(v);
      else out.push({ day, rows: [v] });
    }
    return out;
  }, [items, tab, tz]);

  const plural = t('viewings');
  const noun = t('viewings', 'singular');
  const openNew = () => { setEditing(null); setScheduleOpen(true); };

  const emptyCopy: Record<Tab, { title: string; description: string }> = {
    today: { title: `No ${plural.toLowerCase()} today`, description: 'Book one for a unit a client wants to see — the agent’s calendar is checked for clashes.' },
    upcoming: { title: 'Nothing booked ahead', description: `Scheduled ${plural.toLowerCase()} from now on show up here, earliest first.` },
    past: { title: `No past ${plural.toLowerCase()} yet`, description: 'Completed, cancelled and no-show viewings land here, with the client’s feedback.' },
  };

  return (
    <>
      <Topbar
        title={plural}
        breadcrumb={`${t('operations')} / ${plural}`}
        description="Who’s seeing which unit, when — and how it went."
        right={<Button iconLeft={<Plus size={15} />} onClick={openNew}>Schedule {noun.toLowerCase()}</Button>}
      />

      <div className="w-full space-y-5 px-4 py-6 sm:px-7 sm:py-7">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
          <Kpi label="Today" value={summary?.today} icon={<CalendarCheck size={16} />} tone="gold" hint="Not counting cancelled" onClick={() => setTab('today')} active={tab === 'today' && !filtered} />
          <Kpi label="Upcoming" value={summary?.upcoming} icon={<CalendarClock size={16} />} tone="blue" hint="Scheduled from now on" onClick={() => setTab('upcoming')} active={tab === 'upcoming' && !filtered} />
          <Kpi
            label="Awaiting feedback"
            value={summary?.awaitingFeedback}
            icon={<MessageSquareText size={16} />}
            tone={summary && summary.awaitingFeedback > 0 ? 'red' : 'green'}
            hint="Past their time, outcome not recorded"
            onClick={() => setTab('past', 'scheduled')}
            active={tab === 'past' && status === 'scheduled'}
          />
          <Kpi label="Done this week" value={summary?.completedThisWeek} icon={<CheckCircle2 size={16} />} tone="green" hint="Completed in the last 7 days" />
          <Kpi label="Hot this week" value={summary?.hotThisWeek} icon={<Flame size={16} />} tone="amber" hint="Clients keen to move" />
        </div>

        <div className="rounded-2xl border border-border bg-surface shadow-soft">
          <div className="flex flex-col gap-3 border-b border-border px-4 pt-2 lg:flex-row lg:items-center lg:justify-between lg:pb-2">
            <div className="-mb-px flex items-center gap-1 overflow-x-auto" role="tablist" aria-label={`${plural} period`}>
              {TABS.map((x) => (
                <button
                  key={x.id}
                  type="button"
                  role="tab"
                  aria-selected={tab === x.id}
                  onClick={() => setTab(x.id)}
                  className={clsx(
                    'shrink-0 border-b-2 px-3 py-2.5 text-sm font-medium transition-colors',
                    tab === x.id ? 'border-primary text-primary' : 'border-transparent text-text-muted hover:text-text-primary',
                  )}
                >
                  {x.label}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2 pb-3 lg:pb-0">
              <div className="w-40">
                <Select value={status} onChange={(e) => { setStatus(e.target.value as ViewingStatus | ''); setPage(1); }} aria-label="Status">
                  <option value="">{tab === 'upcoming' ? 'Scheduled' : 'Any status'}</option>
                  {VIEWING_STATUSES.filter((s) => !(tab === 'upcoming' && s === 'scheduled')).map((s) => <option key={s} value={s}>{VIEWING_STATUS_LABEL[s]}</option>)}
                </Select>
              </div>
              <div className="w-44">
                <Select value={agent} onChange={(e) => { setAgent(e.target.value); setPage(1); }} aria-label="Agent">
                  <option value="">All agents</option>
                  {user?.userUid && team.some((u) => u.userUid === user.userUid) && <option value={user.userUid}>My viewings</option>}
                  {team.filter((u) => u.userUid !== user?.userUid).map((u) => <option key={u.userUid} value={u.userUid}>{personName(u)}</option>)}
                </Select>
              </div>
            </div>
          </div>

          {error ? (
            <LoadErrorState what={plural.toLowerCase()} onRetry={() => void load()} bare />
          ) : loading && items.length === 0 ? (
            <div className="space-y-2 p-4">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="skeleton h-16 rounded-xl" />)}</div>
          ) : items.length === 0 ? (
            <div className="p-4">
              {filtered ? (
                <NoMatchesState onClear={clearFilters} />
              ) : (
                <EmptyState
                  icon={<CalendarCheck size={28} />}
                  title={emptyCopy[tab].title}
                  description={emptyCopy[tab].description}
                  action={<Button iconLeft={<Plus size={15} />} onClick={openNew}>Schedule {noun.toLowerCase()}</Button>}
                />
              )}
            </div>
          ) : (
            <div className={clsx('transition-opacity', loading && 'opacity-60')}>
              {groups.map((g) => (
                <section key={g.day || 'today'}>
                  {g.day && <h3 className="border-b border-border bg-surface-alt/60 px-4 py-1.5 text-2xs font-semibold uppercase tracking-[0.12em] text-text-muted">{g.day}</h3>}
                  <ul className="divide-y divide-border">
                    {g.rows.map((v) => (
                      <ViewingRow
                        key={v.viewingUid}
                        v={v}
                        tz={tz}
                        busyKey={busyUid}
                        leadLinks={access.leadLinks}
                        contactLinks={access.contactLinks}
                        onComplete={() => setCompleting(v)}
                        onEdit={() => { setEditing(v); setScheduleOpen(true); }}
                        onCancel={() => cancel(v)}
                        onNoShow={() => noShow(v)}
                        onDelete={() => remove(v)}
                      />
                    ))}
                  </ul>
                </section>
              ))}
              {total > LIMIT && <div className="border-t border-border px-4"><Pagination page={page} total={total} limit={LIMIT} onChange={setPage} /></div>}
            </div>
          )}
        </div>
      </div>

      <ScheduleViewingModal
        open={scheduleOpen}
        viewing={editing}
        onClose={() => { setScheduleOpen(false); setEditing(null); }}
        onSaved={() => { setScheduleOpen(false); setEditing(null); refresh(); }}
      />
      <CompleteViewingModal
        viewing={completing}
        onClose={() => setCompleting(null)}
        onSaved={() => { setCompleting(null); refresh(); }}
      />
    </>
  );
}

function ViewingRow({ v, tz, busyKey, leadLinks, contactLinks, onComplete, onEdit, onCancel, onNoShow, onDelete }: {
  v: Viewing;
  tz: string;
  busyKey: string | null;
  leadLinks: boolean;
  contactLinks: boolean;
  onComplete: () => void;
  onEdit: () => void;
  onCancel: () => void;
  onNoShow: () => void;
  onDelete: () => void;
}) {
  const scheduled = v.status === 'scheduled';
  const endMs = new Date(v.scheduledAt).getTime() + v.durationMinutes * 60_000;
  const awaiting = scheduled && endMs < Date.now();
  const busy = (k: string) => busyKey === `${v.viewingUid}:${k}`;
  const customer = v.customerName || v.customerPhone || 'Customer';
  const customerHref = v.leadUid && leadLinks ? `/leads/${v.leadUid}` : v.contactUid && contactLinks ? `/contacts/${v.contactUid}` : null;
  const InterestIcon = v.interest ? INTEREST_ICON[v.interest] : null;

  return (
    <li className="grid grid-cols-[72px_minmax(0,1fr)] gap-x-3 gap-y-2 px-4 py-3 md:grid-cols-[88px_minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,0.8fr)_auto] md:items-center">
      <div className="row-span-3 md:row-span-1">
        <div className={clsx('text-[15px] font-semibold tabular-nums', v.status === 'cancelled' ? 'text-text-muted line-through' : 'text-text-primary')}>{timeInZone(v.scheduledAt, tz)}</div>
        <div className="flex items-center gap-1 text-2xs text-text-muted"><Clock size={10} /> {v.durationMinutes} min</div>
      </div>

      <div className="min-w-0">
        <Link to={`/properties/${v.propertyUid}`} className="block truncate text-[13.5px] font-semibold text-text-primary hover:text-primary hover:underline">
          {v.unit?.label ?? 'Unit removed'}{v.property ? <span className="font-normal text-text-secondary"> · {v.property.name}</span> : null}
        </Link>
        <div className="truncate text-2xs text-text-muted">
          {[v.unit ? unitSpecs(v.unit) : null, placeLine(v.property)].filter(Boolean).join(' · ')}
        </div>
      </div>

      <div className="min-w-0">
        {customerHref ? (
          <Link to={customerHref} className="block truncate text-[13px] font-medium text-text-primary hover:text-primary hover:underline">{customer}</Link>
        ) : (
          <div className="truncate text-[13px] font-medium text-text-primary">{customer}</div>
        )}
        {v.customerPhone && v.customerName && (
          <a href={`tel:${v.customerPhone}`} className="block truncate text-2xs tabular-nums text-text-muted hover:text-primary">{v.customerPhone}</a>
        )}
      </div>

      <div className="min-w-0 text-xs text-text-secondary">
        <div className="truncate">{v.agentName || <span className="text-text-muted">No agent</span>}</div>
      </div>

      <div className="col-span-2 flex flex-wrap items-center justify-between gap-2 md:col-span-1 md:justify-end">
        <div className="flex flex-wrap items-center gap-1.5">
          <StatusPill status={v.status} label={VIEWING_STATUS_LABEL[v.status]} variant={VIEWING_STATUS_VARIANT[v.status]} />
          {awaiting && <Pill className="bg-warning-soft text-warning">Awaiting feedback</Pill>}
          {v.interest && InterestIcon && <Pill className={INTEREST_CLASS[v.interest]}><InterestIcon size={11} /> {INTEREST_LABEL[v.interest]}</Pill>}
        </div>
        <div className="flex items-center">
          {v.status !== 'cancelled' && (
            <button type="button" onClick={onComplete} disabled={!!busyKey} title={v.status === 'completed' ? 'Edit feedback' : 'Record outcome'} aria-label={v.status === 'completed' ? 'Edit feedback' : 'Record outcome'} className={clsx(iconBtn, 'hover:text-success')}>
              {v.status === 'completed' ? <Pencil size={15} /> : <CheckCircle2 size={15} />}
            </button>
          )}
          {scheduled && (
            <>
              <button type="button" onClick={onEdit} disabled={!!busyKey} title="Reschedule" aria-label="Reschedule" className={clsx(iconBtn, 'hover:text-text-primary')}>
                <CalendarClock size={15} />
              </button>
              <button type="button" onClick={onNoShow} disabled={!!busyKey} title="No-show" aria-label="Mark as no-show" className={clsx(iconBtn, 'hover:text-warning')}>
                {busy('noshow') ? <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" /> : <UserX size={15} />}
              </button>
              <button type="button" onClick={onCancel} disabled={!!busyKey} title="Cancel viewing" aria-label="Cancel viewing" className={clsx(iconBtn, 'hover:text-danger')}>
                {busy('cancel') ? <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" /> : <Ban size={15} />}
              </button>
            </>
          )}
          <button type="button" onClick={onDelete} disabled={!!busyKey} title="Delete" aria-label="Delete viewing" className={clsx(iconBtn, 'hover:text-danger')}>
            {busy('delete') ? <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" /> : <Trash2 size={15} />}
          </button>
        </div>
      </div>

      {(v.feedback || v.notes) && (
        <p className="col-span-2 line-clamp-2 text-xs text-text-secondary md:col-span-5 md:pl-[100px]">
          {v.feedback ? <><span className="font-semibold text-text-primary">Feedback:</span> {v.feedback}</> : <><span className="font-semibold text-text-primary">Notes:</span> {v.notes}</>}
        </p>
      )}
    </li>
  );
}

function CompleteViewingModal({ viewing, onClose, onSaved }: { viewing: Viewing | null; onClose: () => void; onSaved: () => void }) {
  const [interest, setInterest] = useState<ViewingInterest | ''>('');
  const [feedback, setFeedback] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!viewing) return;
    setInterest(viewing.interest ?? '');
    setFeedback(viewing.feedback ?? '');
  }, [viewing]);

  if (!viewing) return null;
  const who = viewing.customerName || viewing.customerPhone || 'the customer';

  const save = async () => {
    setBusy(true);
    try {
      const body: CompleteViewingInput = {};
      if (interest !== (viewing.interest ?? '')) body.interest = interest || null;
      if (feedback.trim() !== (viewing.feedback ?? '').trim()) body.feedback = feedback.trim();
      await realEstateService.completeViewing(viewing.viewingUid, body);
      toast.success(viewing.status === 'completed' ? 'Feedback saved' : 'Viewing completed');
      onSaved();
    } catch (e: unknown) {
      if (!(e as { response?: unknown })?.response) toast.error('Could not save — check your connection');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={() => !busy && onClose()} title={viewing.status === 'completed' ? 'Viewing feedback' : 'How did the viewing go?'} widthClass="max-w-md">
      <p className="mb-4 text-[13px] text-text-secondary">
        {viewing.unit?.label ?? 'Unit'}{viewing.property ? ` · ${viewing.property.name}` : ''} with <span className="font-semibold text-text-primary">{who}</span>
      </p>
      <FormField label="Interest">
        <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Interest">
          {(['hot', 'warm', 'cold'] as const).map((k) => {
            const Icon = INTEREST_ICON[k];
            const on = interest === k;
            return (
              <button
                key={k}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => setInterest(on ? '' : k)}
                className={clsx(
                  'flex flex-col items-center gap-1 rounded-xl border px-3 py-3 text-xs font-semibold transition-colors',
                  on ? clsx('border-transparent', INTEREST_CLASS[k]) : 'border-border text-text-secondary hover:border-border-strong',
                )}
              >
                <Icon size={18} />
                {INTEREST_LABEL[k]}
              </button>
            );
          })}
        </div>
      </FormField>
      <FormField label="Feedback" hint="What they liked, objections, next step">
        <Textarea rows={4} maxLength={4000} value={feedback} onChange={(e) => setFeedback(e.target.value)} autoFocus />
      </FormField>
      <div className="mt-2 flex justify-end gap-2 border-t border-border pt-4">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="primary" onClick={() => void save()} loading={busy}>{viewing.status === 'completed' ? 'Save' : 'Mark completed'}</Button>
      </div>
    </Modal>
  );
}
